import type { FC } from 'react'
import type { IconButtonProps } from '@chakra-ui/react'

import { useRef, useMemo } from 'react'
import tw, { styled } from 'twin.macro'
import { useRouter } from 'next/router'
import CopyToClipboard from 'react-copy-to-clipboard'

import {
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  useDisclosure,
} from '@chakra-ui/react'

import useToast from '@/hooks/useToast'

import ShareIcon from '../../svgs/share.svg'

const ShareLink = styled.a`
  ${tw`
    flex flex-row gap-2 items-center
    font-primary text-base text-black cursor-pointer
  `}

  img {
    ${tw`w-4 h-4 select-none`}
  }

  span {
    border-bottom: 1px solid transparent;
    :hover {
      border-bottom: 1px solid #000;
    }
  }
`

export type ShareButtonProps = {
  label?: string
  url?: string
  text?: string
} & Pick<IconButtonProps, "variant" | "colorScheme" | "size">

const ShareButton: FC<ShareButtonProps> = ({
  label = 'Share',
  variant = 'secondary',
  colorScheme,
  size,
  url,
  text,
}) => {
  const router = useRouter()
  const SocialList = useMemo(() => {
    const target = encodeURIComponent(url || `https://phala.world${router.asPath}`)
    const shareText = encodeURIComponent(text || 'Come and join @PhalaWorld - the first NFT digital progression platform built on @PhalaNetwork.')
    return [
      {
        name: 'Twitter',
        link: `https://twitter.com/share?url=${target}&text=${shareText}`,
        icon: '/static/socialIcons/twitter.svg',
      },
      {
        name: 'Telegram',
        link: `https://t.me/share/url?url=${target}`,
        icon: '/static/socialIcons/telegram.svg',
      },
      {
        name: 'Facebook',
        link: `https://www.facebook.com/sharer/sharer.php?u=${target}`,
        icon: '/static/socialIcons/facebook.svg',
      },
    ]
  }, [router, url, text])
  
  const toast = useToast({ title: 'Copied!' })

  const ref = useRef(null)
  const { onOpen, onClose, isOpen } = useDisclosure()

  return (
    <Popover
      placement="right"
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      initialFocusRef={ref}
    >
      <PopoverTrigger>
        <IconButton
          aria-label={label}
          variant={variant}
          colorScheme={colorScheme}
          size={size}
          icon={<ShareIcon tw="w-[60%] h-[60%] fill-current" />}
        />
      </PopoverTrigger>
      <PopoverContent>
        <PopoverArrow />
        <PopoverBody>
          <div tw="flex flex-col gap-1">
            {SocialList.map((item) => (
              <ShareLink
                key={item.name}
                onClick={() => onClose()}
                href={item.link}
                target="_blank"
              >
                <img src={item.icon} alt={item.name} />
                <span>{item.name}</span>
              </ShareLink>
            ))}
            <CopyToClipboard
              text={url || location.href}
              onCopy={() => {
                toast()
                onClose()
              }}
            >
              <ShareLink>
                <img src="/static/socialIcons/copy.svg" alt="copy URL" />
                <span>Copy URL</span>
              </ShareLink>
            </CopyToClipboard>
          </div>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}

export default ShareButton